/**
 * Riepilogo di fine allenamento, calcolato dai dati GIÀ in memoria al
 * completamento (nessuna query aggiuntiva). Logica separata dalla UI:
 * il popup di fine workout e la futura card condivisibile riusano questo.
 */

/**
 * @param {Array} exerciseLogs log costruiti al completamento (WorkoutExecution/FreeWorkout)
 * @param {number} durationMinutes durata dell'allenamento in minuti
 * @returns {{ durationMinutes, exercisesCount, setsCount, totalVolumeKg }}
 */
export const buildWorkoutSummary = (exerciseLogs, durationMinutes) => {
  const logs = Array.isArray(exerciseLogs) ? exerciseLogs : [];

  const exerciseKeys = new Set(
    logs.map((l) =>
      l.exerciseId
        ? `ex-${l.exerciseId}`
        : l.exerciseTemplateId
          ? `tpl-${l.exerciseTemplateId}`
          : `name-${l.exerciseName || '?'}`
    )
  );

  // Volume = Σ peso × ripetizioni sulle sole serie di forza con dati completi.
  // Escluse: serie senza peso o senza reps, cardio (weight assente) e time-based
  // (duration_seconds/durationSeconds: lì "reps" contiene i secondi, non ripetizioni).
  // weightedSetsCount = le serie che concorrono al volume (stesso criterio,
  // stesso loop: serve a chooseHeroMetric per giudicarne la rappresentatività).
  let totalVolumeKg = 0;
  let weightedSetsCount = 0;
  for (const l of logs) {
    if (l.duration_seconds || l.durationSeconds || l.duration_minutes || l.durationMinutes) continue;
    const w = Number(l.weight);
    const r = Number(l.reps);
    if (Number.isFinite(w) && w > 0 && Number.isFinite(r) && r > 0) {
      totalVolumeKg += w * r;
      weightedSetsCount += 1;
    }
  }

  return {
    durationMinutes: Number(durationMinutes) || 0,
    exercisesCount: exerciseKeys.size,
    setsCount: logs.length,
    totalVolumeKg: Math.round(totalVolumeKg),
    weightedSetsCount,
  };
};

// Sopra questa soglia il volume passa alle tonnellate; sotto resta in kg
// con separatore delle migliaia ("1.450 kg"). Parametrizzabile qui.
const VOLUME_TONNES_THRESHOLD_KG = 10000;

/**
 * Parti separate (valore, unità) dello stesso formato di formatVolume:
 * unica fonte di verità per popup, card condivisibile e futuri usi.
 * Null/0 → null.
 */
export const formatVolumeParts = (kg) => {
  const v = Number(kg);
  if (!Number.isFinite(v) || v <= 0) return null;
  if (v >= VOLUME_TONNES_THRESHOLD_KG) {
    const t = v / 1000;
    const rounded = Math.round(t * 10) / 10;
    return {
      value: String(Number.isInteger(rounded) ? rounded : rounded.toFixed(1)).replace('.', ','),
      unit: 't',
    };
  }
  // Separatore migliaia "." esplicito: Intl con locale it-IT non è garantito
  // in ogni runtime e il formato deve essere identico ovunque.
  return {
    value: String(Math.round(v)).replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
    unit: 'kg',
  };
};

/** "980 kg", "1.450 kg", "12,5 t" oltre soglia. Null/0 → null (la UI mostra "—"). */
export const formatVolume = (kg) => {
  const parts = formatVolumeParts(kg);
  return parts ? `${parts.value} ${parts.unit}` : null;
};

/** "52 min", "1h 05m" da 60 minuti in su. */
export const formatDuration = (minutes) => {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return `${h}h ${String(rest).padStart(2, '0')}m`;
};
